<template>
    <div class="adapters_overlay_panel__container hidden">
        <OverlayPanel
            ref="overlayPanel"
            :dismissable="dismissable"
            :showCloseIcon="showCloseIcon"
            :appendTo="appendTo"
            :baseZIndex="baseZIndex"
            :autoZIndex="autoZIndex"
            :breakpoints="breakpoints"
            :pt="getPT"
            :class="getAlignOverlayClass"
        >
            <!-- закрывающий слот -->
            <template v-if="showCloseIcon" #closeicon>
                <slot name="closeicon"></slot>
            </template>
            <!-- loader -->
            <AdaptersLoader :loading="isLoading" />
            <!-- error -->
            <Message v-if="errorMessage" severity="error" :closable="false">
                <div v-html="errorMessage"></div>
            </Message>
            <!-- content -->
            <slot></slot>
        </OverlayPanel>
    </div>
</template>

<script>
import OverlayPanel from "primevue/overlaypanel";
import AdaptersLoader from "./Loader.vue";
import Message from "primevue/message";
import {nextTick} from "vue";

/**
 * Два варианта вызова:
 *  через прямое обращение к методам (ref)
 *  через $emitter и ID слушателя
 */
export default {
    name: "AdaptersOverlayPanel",
    props: {
        // custom
        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
        // выравнивание overlay относительно элемента
        alignOverlay: {
            type: String,
            // top - дефолтные стили компонента. Кастомные стили прописаны для right и bottom-right
            enum: ['top', 'left', 'right', 'bottom', 'bottom-right'],
            required: false,
            default: 'top',
        },
        errorMessage: {
            type: String,
            required: false,
            default: null,
        },
        listenerId: {
            type: String,
            required: false,
            default: null,
        },
        hoverUsage: {
            type: Boolean,
            required: false,
            default: false,
        },
        // component
        dismissable: {
            type: Boolean,
            required: false,
            default: true,
        },
        showCloseIcon: {
            type: Boolean,
            required: false,
            default: false,
        },
        appendTo: {
            type: String,
            required: false,
            default: '#keysoApp',
        },
        baseZIndex: {
            type: Number,
            required: false,
            default: 0,
        },
        autoZIndex: {
            type: Boolean,
            required: false,
            default: true,
        },
        breakpoints: {
            type: Object,
            required: false,
            default: null,
        },
        pt: {
            type: Object,
            required: false,
            default: {},
        }
    },
    data() {
        return {
            hover: {
                element: false,
                content: false,
            },
            targetElement: null,
            contentElement: null,
            lastTargetElement: null,
        };
    },
    mounted() {
        if (this.hoverUsage) {
            this.$emitter.on(this.listenerId, event => this.handleHoverUsage(event));
            return;
        }

        if (this.listenerId) {
            this.$emitter.on(this.listenerId, event => this.toggle(event));
        }
    },
    computed: {
        isLoading() {
            return !!this.loading;
        },
        isOpen() {
            return this.$refs.overlayPanel.visible;
        },
        getAlignOverlayClass() {
            return 'adapters_overlay_panel__align_' + this.alignOverlay;
        },
        getPT() {
            return (
                this.hoverUsage && this.listenerId
                    // для ховера добавляем ID в виде слушателя для контейнера, чтобы отслеживать mouseleave
                    ? _.merge({}, this.pt, {root: {id: this.listenerId, }})
                    : this.pt
            )
        },
    },
    methods: {
        handleHoverUsage(event) {
            this.targetElement = event.target;
            this.handleElementMouseEnter(event);
            this.targetElement.addEventListener("mouseenter", this.handleElementMouseEnter);
            this.targetElement.addEventListener("mouseleave", () => setTimeout(() => this.hover.element = false, 200));

            nextTick(() => {
                this.contentElement = document.getElementById(this.listenerId);
                this.contentElement.addEventListener("mouseenter", () => this.hover.content = true);
                this.contentElement.addEventListener("mouseleave", () => setTimeout(() => this.hover.content = false, 200));
            })

        },
        handleHoverLeave() {
            this.targetElement.removeEventListener("mouseenter", this.handleElementMouseEnter);
            this.targetElement.removeEventListener("mouseleave", () => this.hover.element = false);

            nextTick(() => {
                this.contentElement.removeEventListener("mouseenter", () => this.hover.content = true);
                this.contentElement.removeEventListener("mouseleave", () => setTimeout(() => this.hover.content = false, 200));
            })

            this.hide();
        },
        handleElementMouseEnter(event) {
            this.hover.element = true;
            this.show(event);
        },
        resolveTargetElement(event) {
            return (
                event?.currentTarget
                || this.$refs.overlayPanel?.target
                || event?.target?.closest?.('button, [role="button"], .p-button')
                || event?.target
                || this.targetElement
                || this.lastTargetElement
            );
        },
        resolveOverlayElement() {
            const overlayRef = this.$refs.overlayPanel;

            if (overlayRef?.container?.getBoundingClientRect) {
                return overlayRef.container;
            }

            if (overlayRef?.$el?.getBoundingClientRect) {
                return overlayRef.$el;
            }

            return null;
        },
        alignOverlayByTargetEdge(targetElement) {
            if (!['bottom-right'].includes(this.alignOverlay) || !targetElement) {
                return;
            }

            const overlayElement = this.resolveOverlayElement();

            if (!overlayElement) {
                return;
            }

            const targetRect = targetElement.getBoundingClientRect();
            const overlayRect = overlayElement.getBoundingClientRect();
            const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft || 0;

            const left = (
                this.alignOverlay === 'bottom-right'
                    ? targetRect.right + scrollLeft - overlayRect.width
                    : targetRect.left + scrollLeft
            );

            overlayElement.style.left = `${Math.round(left)}px`;
        },
        toggle(event) {
            this.$refs.overlayPanel.toggle(event);
            const targetElement = this.resolveTargetElement(event);
            this.lastTargetElement = targetElement;

            nextTick(() => this.alignOverlayByTargetEdge(targetElement));
        },
        show(event) {
            const targetElement = this.resolveTargetElement(event);
            this.lastTargetElement = targetElement;
            this.$refs.overlayPanel.show(event);
            nextTick(() => this.alignOverlayByTargetEdge(targetElement));
        },
        hide() {
            this.$refs.overlayPanel.hide();
        }
    },
    watch: {
        hover: {
            handler() {
                if (!this.hoverUsage) {
                    return;
                }

                if (!this.hover.element && !this.hover.content) {
                    this.handleHoverLeave();
                }
            },
            deep: true
        },
    },
    components: {
        OverlayPanel,
        AdaptersLoader,
        Message,
    }
}
</script>

<style>
.adapters_overlay_panel__align_right {
    margin: -25px 0 0 25px;
}
.adapters_overlay_panel__align_right::before {
    border: 5px solid transparent !important;
    border-right: 4px solid #e9e9e9 !important;
    top: 9px;
    left: -1px;
}
.adapters_overlay_panel__align_right::after {
    border: 4px solid transparent !important;
    border-right: 4px solid var(--surface-a) !important;
    top: 10px;
    left: 0;
}

.adapters_overlay_panel__align_bottom-right {
    margin-top: 10px;
}
.adapters_overlay_panel__align_bottom-right::before {
    left: calc(100% - 24px) !important;
}
.adapters_overlay_panel__align_bottom-right::after {
    left: calc(100% - 23px) !important;
}
</style>
